// THE DETAILS STEP'S SIZE WORDS WHEN THE RECORD'S SIZE PROBABLY INCLUDES A FINISHED BASEMENT (L1, R-pending-size-label).
//
// Tyler, 2026-10-22, verbatim: "County record a, 3 sentences approved". Option A of docs/ENGINE3_PLAN.md §4 (branch
// engine/e3-pool) and the three sentences offered with it, recorded word for word in
// ~/dev/proppeal-handoff/specs/approved-wording-2026-10-22.md, section L1. These are the WEBSITE's words; the iPhone app
// keeps today's until an app build (its own sentences live in SubjectPropertyView.swift:2362 and :2372).
//
// WHEN EACH IS SAID IS THE SERVER'S DECISION, NEVER THIS CLIENT'S. The server's size-basis guard
// (functions/src/sizeBasis.ts) reads RentCast's size for the home — a figure no client ever holds — and answers on the
// subject as `SubjectView.sqftWording` (contract 2.33.0), only while SIZE_BASIS_GUARD_ENABLED is on. Absent, the step
// says exactly what it said before: the app's two sentences, "County record:" and all (`detailsWording.ts`).
//
//   1  `note: "basementMatch"`   in place of the favourable correction sentence (`details.sqft.favorable`);
//   2  `note: "basementCaption"` under the size field, when the owner has not changed it;
//   3  `recordLabel: "publicRecords"` — "County record:" becomes "Public records:" in BOTH existing sentences. Sentence 3
//      is a RULE over the app's own two sentences, not two new sentences, so it is applied to them here
//      (`withPublicRecordsLabel`) and the app's words keep their one writer, pinned to their Swift lines.
//
// IF A PIN FAILS, DO NOT EDIT THE PIN. A changed word is a new sentence: it goes back to Tyler.

/** What Tyler said, and when. One reply approved all three. */
export const SIZE_LABEL_APPROVAL = {
  by: "Tyler",
  on: "2026-10-22",
  quote: "County record a, 3 sentences approved",
  source: "~/dev/proppeal-handoff/specs/approved-wording-2026-10-22.md, section L1",
} as const;

export interface ApprovedSizeSentence {
  readonly text: string;
  readonly status: "approved";
  /** Tyler's reply, and the sentence's number in it. */
  readonly approval: typeof SIZE_LABEL_APPROVAL & { readonly item: 1 | 2 | 3 };
}

function approved(item: 1 | 2, text: string): ApprovedSizeSentence {
  return { text, status: "approved", approval: { ...SIZE_LABEL_APPROVAL, item } };
}

export const SIZE_BASIS_WORDING = {
  // 1 — replaces `details.sqft.favorable` when the owner's correction matches the county's above-grade area and the
  //     record's size probably includes a basement.
  "sizeBasis.basementMatch": approved(
    1,
    "Public records list {record} sq ft, which looks like it includes a finished basement. Your {corrected} sq ft matches the county's above-grade area, so we won't call this a record error.",
  ),
  // 2 — a caption under the size field when the record's size probably includes a basement and the owner has not
  //     changed it.
  "sizeBasis.basementCaption": approved(
    2,
    "Public records list {record} sq ft. That can include a finished basement; your county notice may show the above-grade area and the basement separately.",
  ),
} as const satisfies Record<string, ApprovedSizeSentence>;

export type SizeBasisWordingId = keyof typeof SIZE_BASIS_WORDING;

/** 3 — the rule, in Tyler's own two strings. */
export const COUNTY_RECORD_LABEL = "County record:";
export const PUBLIC_RECORDS_LABEL = "Public records:";
export const LABEL_RULE_APPROVAL = { ...SIZE_LABEL_APPROVAL, item: 3 } as const;

/** One of the app's two correction sentences with "County record:" read as "Public records:". The label must open the
 *  sentence exactly once; anything else is a thrown error, never a sentence half-relabelled. */
export function withPublicRecordsLabel(sentence: string): string {
  if (!sentence.startsWith(COUNTY_RECORD_LABEL) || sentence.indexOf(COUNTY_RECORD_LABEL, 1) !== -1) {
    throw new Error(`withPublicRecordsLabel: the sentence does not open with "${COUNTY_RECORD_LABEL}" exactly once`);
  }
  return PUBLIC_RECORDS_LABEL + sentence.slice(COUNTY_RECORD_LABEL.length);
}

/** A sentence with its `{name}` slots filled. A slot left unfilled is a thrown error, never printed braces. */
export function sizeWordsWith(id: SizeBasisWordingId, values: Readonly<Record<string, string | number>>): string {
  return SIZE_BASIS_WORDING[id].text.replace(/\{(\w+)\}/g, (_, name: string) => {
    if (!Object.hasOwn(values, name)) throw new Error(`sizeWordsWith(${id}): no value for {${name}}`);
    return String(values[name]);
  });
}
